// ============================================================================
// QUESTION PAGE COMPONENT
// Created by Salvador Janeiro
// ============================================================================

import React, { useState } from 'react';
import { Info, CheckCircle } from 'lucide-react';

const QuestionPage = ({ 
  questions, 
  currentIndex, 
  answers, 
  onAnswer, 
  onNext, 
  onPrevious, 
  onSubmit,
  assessmentType 
}) => {
  const [showInfo, setShowInfo] = useState(false);
  
  const question = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const isLastQuestion = currentIndex === questions.length - 1;
  
  const currentAnswer = answers[question.id];
  const canProceed = question.type === 'multiple' 
    ? Array.isArray(currentAnswer) && currentAnswer.length > 0
    : currentAnswer !== undefined;

  const handleOptionClick = (value) => {
    if (question.type === 'multiple') {
      const current = Array.isArray(currentAnswer) ? currentAnswer : [];
      if (value === 'none') {
        onAnswer(question.id, ['none']);
      } else {
        const filtered = current.filter(v => v !== 'none');
        if (filtered.includes(value)) {
          onAnswer(question.id, filtered.filter(v => v !== value));
        } else {
          onAnswer(question.id, [...filtered, value]);
        }
      }
    } else {
      onAnswer(question.id, value);
    }
  };

  const isSelected = (value) => {
    if (question.type === 'multiple') {
      return Array.isArray(currentAnswer) && currentAnswer.includes(value);
    }
    return currentAnswer === value;
  };

  // Group questions by category for display
  const currentCategory = question.category;
  const categoryQuestions = questions.filter(q => q.category === currentCategory);
  const categoryIndex = categoryQuestions.findIndex(q => q.id === question.id);

  // Category emoji map
  const categoryEmojis = {
    'Transportation': '🚗',
    'Home Energy': '⚡',
    'Diet & Food': '🍽️',
    'Fashion & Shopping': '👕',
    'Waste & Plastic': '♻️',
    'General Consumption': '🛒',
    'Environmental': '🌍',
    'Social': '👥',
    'Governance': '⚖️'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-teal-50 p-4 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600">
              Question {currentIndex + 1} of {questions.length}
            </span>
            <span className="text-sm font-medium text-gray-600">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Category Badge */}
        <div className="mb-4">
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white shadow-sm">
            <span className="text-2xl mr-2">
              {categoryEmojis[currentCategory]}
            </span>
            {currentCategory}
            <span className="ml-2 text-gray-500">
              ({categoryIndex + 1}/{categoryQuestions.length})
            </span>
          </span>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-3">
            {question.question}
          </h2>
          
          {question.info && (
            <div className="relative mb-6">
              <button
                onClick={() => setShowInfo(!showInfo)}
                className="flex items-start space-x-2 p-4 bg-blue-50 rounded-lg w-full text-left hover:bg-blue-100 transition-colors"
              >
                <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-gray-700 font-medium mb-1">Why this matters:</p>
                  <p className="text-sm text-gray-700">{question.info}</p>
                </div>
              </button>
            </div>
          )}

          {/* Answer Options */}
          <div className="space-y-3">
            {question.options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleOptionClick(option.value)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                  isSelected(option.value)
                    ? 'border-green-600 bg-green-50 shadow-md'
                    : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{option.icon}</span>
                  <span className="flex-1 font-medium text-gray-800">{option.label}</span>
                  {isSelected(option.value) && (
                    <CheckCircle size={24} className="text-green-600" />
                  )}
                </div>
              </button>
            ))}
          </div>

          {question.type === 'multiple' && (
            <p className="text-sm text-gray-500 mt-4 italic">
              * Select all that apply
            </p>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <button
            onClick={onPrevious}
            disabled={currentIndex === 0}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              currentIndex === 0
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
            }`}
          >
            Previous
          </button>

          {isLastQuestion ? (
            <button
              onClick={onSubmit}
              disabled={!canProceed}
              className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                canProceed
                  ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg hover:scale-105'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              View Results →
            </button>
          ) : (
            <button
              onClick={onNext}
              disabled={!canProceed}
              className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                canProceed
                  ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg hover:scale-105'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionPage;
